import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BarChart3, Download } from "lucide-react";
import BaseModal from "../modals/BaseModal";
import { pipelineService } from "../../../../services/pipelineService";

const formatCompact = (n) => {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  if (value >= 1e7) return `${(value / 1e7).toFixed(1)}Cr`;
  if (value >= 1e5) return `${(value / 1e5).toFixed(1)}L`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
  return String(value);
};

const formatInrPlain = (n) => {
  const value = Number(n);
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(
    value
  );
};

function MetricCard({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-[#F3F1EA] px-2 lg:px-4 py-2 lg:py-4 text-center shadow-sm">
      <div className="text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wider">
        {label}
      </div>
      <div className="mt-2 text-[10px] md:text-sm font-bold text-gray-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function RoiRow({ label, value, barClass, pct }) {
  return (
    <div className="flex items-center gap-3">
      <div className="lg:w-36 w-18 text-xs lg:text-sm font-medium text-gray-700">{label}</div>
      <div className="flex-1 h-3 rounded-full bg-gray-200 overflow-hidden">
        <div
          className={`h-full ${barClass}`}
          style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
        />
      </div>
      <div className="lg:w-20 w-16 text-right text-xs lg:text-sm font-medium tabular-nums text-gray-900">
        {value}
      </div>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {{ id: string, name: string, handle: string, stageId: string } | null} props.influencer
 * @param {() => void} props.onClose
 * @param {(text: string) => void} props.onAddActivity
 */
export default function PerformanceModal({ open, influencer, onClose, onAddActivity }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [performance, setPerformance] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const itemId = influencer?.__pipelineItemId;

    if (!open || !itemId) {
      setPerformance(null);
      setError("");
      setLoading(false);
      return () => {};
    }

    (async () => {
      setLoading(true);
      setError("");
      try {
        const summary = await pipelineService.getPerformanceSummary({ itemId });
        if (cancelled) return;
        setPerformance(summary);
      } catch (err) {
        if (cancelled) return;
        setPerformance(null);
        setError(err?.message || "Failed to load performance data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, influencer?.__pipelineItemId]);

  const metrics = useMemo(() => {
    const totals = performance?.metrics?.totals || {};
    const payment = performance?.payment || {};

    return {
      views: Number(totals.views || 0),
      likes: Number(totals.likes || 0),
      comments: Number(totals.comments || 0),
      engagementPct: Number(totals.engagementRatePct || 0),
      reach: Number(totals.reach || 0),
      mediaCount: Number(totals.mediaCount || 0),
      spendInr: Number(payment.amountPaid || 0),
      totalDueInr: Number(payment.totalDue || 0),
      remainingInr: Number(payment.amountUnpaid || 0),
    };
  }, [performance]);

  const bars = useMemo(() => {
    const maxValue = Math.max(metrics.totalDueInr, metrics.spendInr, metrics.remainingInr, 1);
    return {
      spendPct: (metrics.spendInr / maxValue) * 100,
      duePct: (metrics.totalDueInr / maxValue) * 100,
      remainingPct: (metrics.remainingInr / maxValue) * 100,
    };
  }, [metrics.remainingInr, metrics.spendInr, metrics.totalDueInr]);

  const onExport = useCallback(() => {
    onAddActivity?.("Performance: export requested");
    window.print();
  }, [onAddActivity]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50"
      >
        Close
      </button>
      <button
        type="button"
        onClick={onExport}
        className="px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 text-sm font-extrabold hover:bg-gray-100 inline-flex items-center gap-2"
      >
        <Download className="w-4 h-4" />
        Export report
      </button>
    </div>
  );

  return (
    <BaseModal
      open={open}
      title={`Performance — ${influencer?.name ?? "Influencer"}`}
      onClose={onClose}
      footer={footer}
      className="lg:max-w-[760px] max-w-[560px]"
    >
      <div className="px-2 py-2 sm:px-5 sm:py-6 bg-white">
        <div className="rounded-xl border border-gray-200 bg-[#F3F1EA] px-2 lg:px-4 py-2 lg:py-4">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 lg:w-10 lg:h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
              <BarChart3 className="lg:w-5 lg:h-5 w-4 h-4 text-gray-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="lg:text-sm text-xs font-extrabold text-gray-900 truncate">
                {influencer?.name ?? "—"}
              </div>
              <div className="lg:text-xs text-xs font-semibold text-gray-500 truncate">
                {influencer?.handle ?? "—"} · Reels · IG Business API connected
              </div>
            </div>
            <span className="text-xs font-extrabold text-orange-700 bg-orange-100 border border-orange-200 px-3 py-1 rounded-full">
              {influencer?.stageId ? influencer.stageId : "—"}
            </span>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 lg:gap-3">
          <MetricCard label="Approved media" value={formatCompact(metrics.mediaCount)} />
          <MetricCard label="Total views" value={formatCompact(metrics.views)} />
          <MetricCard label="Total likes" value={formatCompact(metrics.likes)} />
          <MetricCard label="Total comments" value={formatCompact(metrics.comments)} />
          <MetricCard label="Engagement" value={`${metrics.engagementPct.toFixed(1)}%`} />
          <MetricCard label="Est. reach" value={formatCompact(metrics.reach)} />
          <MetricCard label="Paid" value={`₹${formatInrPlain(metrics.spendInr)}`} />
          <MetricCard label="Remaining" value={`₹${formatInrPlain(metrics.remainingInr)}`} />
          <MetricCard label="Total due" value={`₹${formatInrPlain(metrics.totalDueInr)}`} />
        </div>

        <div className="mt-4 rounded-xl border border-gray-200 bg-[#F3F1EA] p-2 lg:p-4">
          <div className="lg:text-sm text-xs font-extrabold text-gray-900">
            Approved media performance
          </div>
          {loading ? (
            <div className="mt-3 text-xs lg:text-sm font-semibold text-gray-600">
              Loading latest approved media stats...
            </div>
          ) : error ? (
            <div className="mt-3 text-xs lg:text-sm font-semibold text-red-600">{error}</div>
          ) : Array.isArray(performance?.media) && performance.media.length > 0 ? (
            <div className="mt-3 space-y-2 max-h-[260px] overflow-y-auto pr-1">
              {performance.media.map((entry, idx) => (
                <div
                  key={`${entry.platform}_${entry.id || idx}`}
                  className="rounded-xl border border-gray-200 bg-white p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs lg:text-sm font-extrabold text-gray-900 truncate">
                        {entry.title || "Untitled media"}
                      </div>
                      <div className="text-[10px] lg:text-xs font-semibold text-gray-500 mt-1">
                        {(entry.platform || "media").toUpperCase()} · Published {formatDate(entry.publishedAt)}
                      </div>
                    </div>
                    <div className="text-[10px] lg:text-xs font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-1 rounded-full shrink-0">
                      APPROVED
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <MetricCard label="Likes" value={formatCompact(entry?.statistics?.likes || 0)} />
                    <MetricCard label="Comments" value={formatCompact(entry?.statistics?.comments || 0)} />
                    <MetricCard label="Reach" value={formatCompact(entry?.statistics?.reach || 0)} />
                    <MetricCard label="Engagement" value={formatCompact(entry?.statistics?.engagements || 0)} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-3 text-xs lg:text-sm font-semibold text-gray-600">
              No approved media found for this influencer yet.
            </div>
          )}
        </div>

        <div className="mt-5">
          <div className="lg:text-sm text-xs font-extrabold text-gray-900 uppercase tracking-wider">
            Payment breakdown
          </div>
          <div className="mt-3 space-y-2 lg:space-y-3">
            <RoiRow
              label="Paid"
              value={`₹${formatInrPlain(metrics.spendInr)}`}
              barClass="bg-orange-500"
              pct={bars.spendPct}
            />
            <RoiRow
              label="Total due"
              value={`₹${formatInrPlain(metrics.totalDueInr)}`}
              barClass="bg-emerald-500"
              pct={bars.duePct}
            />
            <RoiRow
              label="Remaining"
              value={`₹${formatInrPlain(metrics.remainingInr)}`}
              barClass="bg-blue-600"
              pct={bars.remainingPct}
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
}

